/**
 * THE coercion a client form field goes through on its way INTO a request body.
 * One question: *what number does this typed field put in the payload?*
 *
 * Not a formatter and not the rounding rule — `./money` owns that, and this
 * module imports `toMoneyNumber` from it rather than carrying a second parser.
 * (`server/helpers/money.js` had seven importers, zero index rows, and a brief
 * went out telling an agent to hand-roll a rival money parser beside it. That
 * is the mistake this module exists NOT to repeat.)
 *
 * ── WHY IT EXISTS: `parseFloat` IS PREFIX-LENIENT ─────────────────────
 * 🚨 **`parseFloat('1,000')` is `1`.** Every payload builder on the client used
 * to read a typed amount as `parseFloat(x) || 0`, so a value the server would
 * now refuse with a 400 naming the field arrived instead as a plausible number.
 * A thousandfold under-price, stored with a 2xx and no signal anywhere. The
 * server's money writers were hardened over 2026-09-20/21 to refuse `'1,000'`,
 * `'5abc'`, `'50£'`, `'0x10'` and JSON arrays — but the browser sanitised the
 * value before the request left, so **the new gate could not be exercised from
 * the UI at all.** This module is what lets it fire.
 *
 * 🔑 **`Number.isFinite(parseFloat(x))` AND `Number.isFinite(Number(x))` ARE
 * DIFFERENT PREDICATES, AND ONLY THE SECOND ONE IS THE HOUSE RULE.** This is
 * the sentence to read twice, because the first form LOOKS like the rule and
 * passes review. The house rule — `Number.isFinite`, never `||` — was born from
 * a real money bug ("Off" charged a full week) and is correct about `0`,
 * `NaN` and `Infinity`. It is *necessary but not sufficient* when it is handed
 * `parseFloat`'s output, because `parseFloat` has already thrown the malformed
 * suffix away: `parseFloat('1,000')` is `1`, which is finite, non-negative and
 * non-zero, so a guard written that way waves the thousandfold under-price
 * straight through. `Number('1,000')` is `NaN`, which is the answer we want.
 * `toMoneyNumber` is built on `Number`, which is why it is the one to call.
 *
 * ── THE THREE RULES THAT ARE NOT NEGOTIABLE ───────────────────────────
 * ⚖️ **1. A REAL TYPED `0` MUST STILL SEND.** Ruled twice by the owner: £0 is a
 * legitimate marketplace rate, and an explicit zero survives a date change.
 * `toMoneyNumber('0')` is `0`, and `0` is only ever replaced here when the
 * field was BLANK — never because it was falsy. Nothing in this file uses `||`.
 *
 * ⚖️ **2. BLANK AND MALFORMED ARE DIFFERENT ANSWERS.** Blank means "the user
 * said nothing", and each column decides what that means: `weekly_rate` and
 * `computer_rental` DEFAULT 0 (nullable, but zero-meaning), so blank is `0`; the
 * 6th/7th-day premium overrides are nullable with no default, so blank is
 * `null` (UNSTATED — the server applies 1.5×/2×). Malformed means "the user
 * said something we cannot read", and that is ALWAYS `null`, so the server's
 * gate fires and names the field instead of the client inventing a number.
 *
 * 🪤 **3. WHITESPACE-ONLY IS BLANK, and forgetting this INVERTS rule 2.**
 * `Number('   ')` is `0`, not `NaN` — so a bare `toMoneyNumber` on a spaces-only
 * field answers `0`, and on a nullable premium column that is the difference
 * between "unstated" and a typed zero the save is supposed to refuse. Every
 * entry point here trims before it decides, which `parseFloat` got right by
 * accident and `Number` does not.
 *
 * 🔴 **THIS IS FEEDBACK, NEVER A GATE.** The server refuses independently and
 * is the authority. What this module buys is that a refusal is now REACHABLE
 * from a browser and lands as a 400 naming the field, instead of being silently
 * converted into a wrong number that stores with a 2xx.
 */
import { toMoneyNumber, roundMoney } from './money';

/** Blank = nothing typed. `''`, whitespace-only, `null`, `undefined`. */
function isBlank(value) {
  if (value === null || value === undefined) return true;
  return typeof value === 'string' && value.trim() === '';
}

/**
 * The value a NUMERIC-BUT-NOT-MONEY field contributes to a payload —
 * a quantity, an allocation, a day count.
 *
 * Blank → `blankDefault`. Parseable → the number, UNROUNDED, `0` included.
 * Malformed → `null`, so the server's gate fires and names the field.
 *
 * ═════ 🚨 WHY THIS EXISTS AS A SEPARATE EXPORT (2026-09-22) ═════
 * **SHAPE and SCALE are two screens and only the first one is shared.** Every
 * field on these forms needs the shape screen — `parseFloat('1,000')` is `1`
 * on a quantity box exactly as it is on a price box, and a thousandfold
 * under-count is no better than a thousandfold under-price. **The 2dp SCALE
 * rule is money's alone.**
 *
 * 🔴 IT WAS NOT SEPARATE, AND THAT WAS A LIVE WIDENING OF AN UNRELATED GUARD.
 * `toPayloadQuantity` and both `default_allocation` builders went through
 * `toPayloadAmount`, so they inherited `roundMoney`. `default_allocation` is
 * governed by `helpers/allocation.js` — **a FRACTION of a five-day week, legal
 * values `{0, 0.2, 0.4, 0.6, 0.8, 1}`** — with no CHECK constraint on either
 * engine, so that helper is the only gate. Rounding the typed value first made
 * the server **ACCEPT at 200 a value it is written to REFUSE**. Nothing in the
 * money change mentioned allocation; the guard was widened by a helper it
 * shares. 🔑 **When you add a rule to a shared helper, enumerate what else
 * flows through it** — the blast radius of a money change is every column that
 * borrowed the money parser.
 * 📏 **MEASURED, the values that really flipped 400 → 201:** `0.599 → 0.6`,
 * `0.999 → 1`, `0.204 → 0.2` and **`1e-7 → 0`** — the last being the sharpest,
 * because `0` is a real allocation state ("Off"), so a typo is rounded into a
 * deliberate-looking setting instead of an error.
 * 🔻 **THIS DOCBLOCK SAID `2.999 → 3` UNTIL 2026-09-22 AND THAT INSTANCE IS
 * IMPOSSIBLE** — an allocation is a fraction, so `2.999` and `3` are BOTH
 * refused. The wrong instance came from a task brief, was never measured, and
 * reached four artifacts before anyone checked it. **An unmeasured defect
 * instance propagates exactly as fast as a correct one.**
 *
 * 🪤 It still delegates the SHAPE screen to `money.js#toMoneyNumber`, and that
 * is deliberate rather than an oversight: that function is the house's one
 * answer to *"is this string a number a human typed"* (it refuses `'1,000'`,
 * booleans, radix literals and objects), and a second copy of it under a
 * non-money name is the duplication this repo keeps paying for. **What must
 * never be borrowed is `roundMoney`.**
 *
 * 🚨 **"SHAPE ONLY" MEANS THE *MONEY* SHAPE, AND THAT IS NOT A NEUTRAL NUMBER
 * SCREEN — DO NOT REUSE THIS FOR AN INTEGER ID.** It inherits every one of
 * `toMoneyNumber`'s money-shaped decisions: radix literals are REFUSED
 * (`'0x10'` → `null`, correct for an amount) but **scientific notation is
 * ACCEPTED**, so `'1e3'` answers `1000`. On a quantity or an allocation that is
 * right — both are magnitudes a human might legitimately write that way. On an
 * `order_id`, a `department_id` or any other identifier it is a defect: an id
 * is a TOKEN, not a magnitude, and `'1e3'` is not a way of writing `1000` in an
 * id field. **An id needs its own predicate** (an integer test over the raw
 * string), not this one and not `parseInt`, which is a different kind of
 * lenient again — `parseInt('12abc')` is `12`, the trap root `CLAUDE.md`
 * already records for `?department_id=`.
 */
export function toPayloadNumber(value, blankDefault = 0) {
  if (isBlank(value)) return blankDefault;
  const parsed = toMoneyNumber(value);
  return parsed === null ? null : parsed;
}

/**
 * The value a money field contributes to a payload.
 * Blank → `blankDefault` (the CALLER's column-specific decision).
 * Parseable → the number, `0` included.
 * Malformed → `null`, so the server's gate fires and names the field.
 */
export function toPayloadAmount(value, blankDefault = 0) {
  if (isBlank(value)) return blankDefault;
  const parsed = toMoneyNumber(value);
  if (parsed === null) return null;
  // 💷 **THE 2dp ROUND, AND IT IS HERE BECAUSE THIS IS THE CHOKE POINT
  // (2026-09-22).** `ui/CurrencyInput` snaps on BLUR — and **blur is not the
  // only exit from a field.** Measured: pressing Enter in a focused money input
  // fires no blur at all, so the raw `19.3333333` went straight into the body;
  // a value hydrated from the API and submitted untouched never blurs either.
  //
  // 🔑 Chasing exits in the component is a losing game — blur, Enter, a button
  // that does not blur, a programmatic submit — and the NEXT one gets missed
  // too. A payload builder runs on submit whatever the gesture was, so rounding
  // HERE closes every exit by construction. It is also this module's own stated
  // job: *"the only answer to what number does this typed form field put in the
  // REQUEST BODY"*.
  //
  // 🪤 **AN OVERFLOWING VALUE KEEPS ITS MAGNITUDE.** `roundMoney` answers
  // `null` when the x100 step overflows, and letting that fall through would
  // send `null` — which the server refuses as "not an amount" rather than as
  // "too large", telling the caller the wrong thing. Pinned by §0.7 of
  // `moneyPayload.rounds.test.js`.
  //
  // 🔴 The server is still the authority — every one of these columns rounds at
  // its own write boundary. What this closes is the CLIENT's broken promise,
  // and the live client-side derivations that read the same state.
  const rounded = roundMoney(parsed);
  return rounded === null ? parsed : rounded;
}

/**
 * The value a QUANTITY field contributes to a payload.
 *
 * 🚨 **`quantity: 0` MEANS ONE, and that is a RULING, not a leftover `||`.**
 * Root `CLAUDE.md`'s `hireCost.js` row: *"`quantity: 0` means ONE (matching the
 * non-hire `quantity || 1`) — the one case where a falsy value IS the absence
 * marker"*, and `hireLineQuantity` on both tiers reads it that way. So a
 * quantity is the ONE money-adjacent field where mapping a real zero to a
 * default is correct, and "fixing" it to preserve the zero would break the
 * ruling AND desync the payload from the cost helper that prices it.
 *
 * 🔑 What changes here is only the MALFORMED case: `'1,000'` used to become `1`
 * via `parseFloat` — indistinguishable from a deliberate single unit — and now
 * becomes `null` for the server to refuse. Blank and `0` still mean one.
 *
 * 🚨 **IT BUILDS ON `toPayloadNumber`, NEVER ON `toPayloadAmount` — A QUANTITY
 * IS NOT MONEY AND MUST NOT BE ROUNDED (corrected 2026-09-22).** It routed
 * through the money builder for one day and therefore through `roundMoney`,
 * which put it in direct contradiction with
 * `money-write-boundary-column-manifest.test.js`, where `order_items.quantity`
 * is graded NOT_MONEY and the reason says in terms that it *"must never be
 * routed through a money helper"*. **A rule and a test asserted opposite
 * things, and the test was the one that was wrong** — §0.8 of
 * `moneyPayload.rounds.test.js` PINNED `2.6666666 → 2.67` as intended
 * behaviour, so the contradiction was not merely unnoticed, it was ratcheted.
 * 🕶️ That is the failure mode the manifest family exists to stop, arriving
 * from the other direction: not a scan with a blind spot, but a pin asserting
 * the opposite of a declared rule, each green in its own file.
 */
export function toPayloadQuantity(value) {
  const n = toPayloadNumber(value, 1);
  return n === 0 ? 1 : n;
}

/**
 * Did the user type something we cannot read? For VALIDATORS only — the ones
 * that show a message and leave the form exactly as the user left it.
 *
 * 🔑 Blank is NOT malformed. A validator that refuses blank would refuse every
 * optional rate on the form; deciding what blank means is the payload
 * builder's job, above.
 */
export function isMalformedAmount(value) {
  if (isBlank(value)) return false;
  return toMoneyNumber(value) === null;
}

// ── ARCHAEOLOGY (moved from root CLAUDE.md 2026-09-25) ──────────────────────
// The root `CLAUDE.md` Shared Modules row for this file keeps every RULE (shape
// and scale are two screens, `toPayloadNumber` never rounds, enumerate what
// else flows through a shared builder, a validator asks the builder's question,
// `Number.isFinite(Number(x))` is the house rule, blank vs malformed, a typed
// `0` sends `0`, whitespace is blank, feedback never a gate) and points here.
// Moved VERBATIM from that row:
//
// 📏 **MEASURED flips: `0.599→0.6`, `0.999→1`, `0.204→0.2`, `1e-7→0`** — the
// last is the sharpest, because `0` is the real "Off" state, so a typo is
// rounded into a deliberate-looking setting. 🕶️ **The instance first written
// here was `2.999→3`, which is IMPOSSIBLE** (both are out of range and both
// 400); it came from a task brief, was never measured, and reached four
// artifacts — **an unmeasured defect instance propagates exactly as fast as a
// correct one.**
//
// On a validator asking the builder's question: `parseFloat('1,000')` is `1`,
// so the marketplace rate gate passed a value its own payload line turned into
// `null`, and the user got a bare API error instead of the form's inline
// message.
//
// The server's money writers were hardened 2026-09-20/21; the browser
// sanitised the value before the request left, so **the new gate could not be
// exercised from the UI at all** until these call sites changed.
//
// On the two predicates: — the first is how `crewMoneyFormError` and
// `nullableRate`, two guards written to catch bad money, both waved `'1,000'`
// through while reading as correct in review. This is the first recorded site
// where `Number.isFinite` was *necessary but not sufficient*, because of what
// it was being handed.
